/**
 * External dependencies
 */
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import {
  Card,
  CardContent,
  CardFooter,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/card';
import { Checkbox } from '@art-of-hospitality-solutions-llc/ui-kit/components/checkbox';
import { Heading } from '@art-of-hospitality-solutions-llc/ui-kit/components/heading';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription,
  FormMessage,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/form';
import {
  Alert,
  AlertTitle,
  AlertDescription,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/select';
import { toastSuccess } from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';

import Tiptap from '@Components/TipTap';
import Breadcrumbs from '@Components/Breadcrumbs';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import service from '../../../service';
import { CreateVenuePayload } from '@Types/api';

const VenueCategorySchema = z.enum([
  'HOTEL',
  'RESTAURANT',
  'BAR_LOUNGE',
  'NO_CATEGORY',
]);

const FormSchema = z.object({
  name: z.string().min(2, {
    message: 'A name is required.',
  }),
  description: z.string().optional(),
  teaser: z.string().optional().nullable(),
  introText: z.string().optional().nullable(),
  openTableBookingUrl: z
    .string()
    .optional()
    .transform((val) => (val && val.trim().length ? val.trim() : undefined))
    .pipe(z.httpUrl().optional()),
  openTableId: z.number().int().positive().nullable(),
  tripleSeatId: z.number().int().positive().nullable(),
  contactFishIds: z.string().default(''),
  category: VenueCategorySchema,
  membersOnly: z.boolean(),
  sendShiftReport: z.boolean(),
  enabled: z.boolean(),
});

// Input type (before zod transforms/defaults) — used for the form's field values.
type FormInput = z.input<typeof FormSchema>;
// Output type (after transforms/defaults) — what handleSubmit receives.
type FormValues = z.infer<typeof FormSchema>;

export default function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<FormInput, unknown, FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: '',
      description: '',
      teaser: '',
      introText: '',
      openTableBookingUrl: '',
      openTableId: null,
      tripleSeatId: null,
      contactFishIds: '',
      category: undefined,
      membersOnly: false,
      sendShiftReport: false,
      enabled: true,
    },
  });

  const onSubmit = async (values: FormValues) => {
    const {
      name,
      description,
      teaser,
      introText,
      enabled,
      openTableBookingUrl,
      openTableId,
      tripleSeatId,
      contactFishIds,
      category,
      membersOnly,
      sendShiftReport,
    } = values;

    const body: CreateVenuePayload = {
      name,
      ...(description && description.trim()
        ? { description: description.trim() }
        : {}),
      openTableBookingUrl,
      ...(openTableId != null ? { openTableId } : {}),
      ...(tripleSeatId != null ? { tripleSeatId } : {}),
      contactFishIds: contactFishIds
        ? contactFishIds
            .split('\n')
            .map((email) => email.trim())
            .filter(Boolean)
        : [],
      ...(enabled ? {} : { disabledAt: new Date().toISOString() }),
      category,
      membersOnly,
      sendShiftReport,
    };
    if (teaser != null && teaser.trim()) {
      body.teaser = teaser.trim();
    }
    if (introText != null && introText.trim()) {
      body.introText = introText.trim();
    }

    // clear any previous root error before submitting
    form.clearErrors('root');

    try {
      const venue = await service.createVenue(body);

      if (!venue) {
        // treat undefined / falsy as an error
        throw new Error('No venue returned from API');
      }

      await queryClient.invalidateQueries({ queryKey: ['venues'] });
      toastSuccess('Venue created successfully.');
      navigate('/venues');
    } catch (error) {
      console.error('Error creating venue:', error);
      form.setError('root', {
        type: 'server',
        message:
          'We were unable to create this venue. Please check your details and try again.',
      });
    }
  };

  const rootError = form.formState.errors.root?.message;

  return (
    <div className="w-full">
      <Breadcrumbs
        navList={[
          { name: 'Dashboard', href: '/' },
          { name: 'Venues', href: '/venues' },
          { name: 'Create', href: '/venues/create' },
        ]}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="mb-4">
            <Heading level="h2">Create A Venue</Heading>
          </div>
          <Card>
            <CardContent>
              {/* Top-level error banner */}
              {rootError && (
                <div className="mb-4">
                  <Alert variant="destructive">
                    <AlertTitle>Something went wrong</AlertTitle>
                    <AlertDescription>{rootError}</AlertDescription>
                  </Alert>
                </div>
              )}

              <div className="space-y-6 py-3">
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Venue name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Description */}
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Tiptap
                          value={field.value || ''}
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          placeholder="Describe this venue..."
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Teaser */}
                <FormField
                  control={form.control}
                  name="teaser"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teaser</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Short summary or tagline for this venue."
                          {...field}
                          value={field.value || ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Intro text */}
                <FormField
                  control={form.control}
                  name="introText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Intro text</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Intro message sent from the stylist when a wedding is booked at this venue."
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* OpenTable Booking URL */}
                <FormField
                  control={form.control}
                  name="openTableBookingUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OpenTable Booking URL</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* OpenTable Restaurant ID */}
                <FormField
                  control={form.control}
                  name="openTableId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>OpenTable Restaurant ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Numeric OpenTable restaurant ID used for reservations.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Tripleseat Location ID */}
                <FormField
                  control={form.control}
                  name="tripleSeatId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tripleseat Location ID</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ''
                                ? null
                                : Number(e.target.value),
                            )
                          }
                        />
                      </FormControl>
                      <FormDescription>
                        Numeric Location ID from Tripleseat used for event
                        bookings.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Contact Fish */}
                <FormField
                  control={form.control}
                  name="contactFishIds"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Shift Report Contacts</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter contact emails, one per line..."
                          className="min-h-[100px]"
                        />
                      </FormControl>
                      <FormDescription>
                        Emails of contacts who will receive shift reports (one
                        per line).
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Category */}
                <FormField
                  control={form.control}
                  name="category"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Category</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                      >
                        <FormControl>
                          <SelectTrigger className="w-70">
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="HOTEL">Hotel</SelectItem>
                          <SelectItem value="RESTAURANT">Restaurant</SelectItem>
                          <SelectItem value="BAR_LOUNGE">
                            Bar / Lounge
                          </SelectItem>
                          <SelectItem value="NO_CATEGORY">
                            No Category
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* MembersOnly */}
                <FormField
                  control={form.control}
                  name="membersOnly"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                        </FormControl>
                        <FormLabel>Members Only</FormLabel>
                      </div>
                      <FormDescription>
                        Enable for Venues exclusive to Club Members
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* SendShiftReport */}
                <FormField
                  control={form.control}
                  name="sendShiftReport"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                        </FormControl>
                        <FormLabel>Send Shift Report</FormLabel>
                      </div>
                      <FormDescription>
                        Enable to generate shift summaries for this venue
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Enabled */}
                <FormField
                  control={form.control}
                  name="enabled"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center gap-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) =>
                              field.onChange(checked === true)
                            }
                          />
                        </FormControl>
                        <FormLabel>Enabled</FormLabel>
                      </div>
                      <FormDescription>
                        Disabling a venue hides it from users.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>

            <CardFooter className="flex gap-2">
              <Button
                type="submit"
                variant="primary"
                loading={form.formState.isSubmitting}
              >
                Create
              </Button>
              <Button variant="link" asChild>
                <Link to="/venues">Cancel</Link>
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}
